import React, { useState, useEffect } from 'react'
import { getAllSupportChats, subscribeToSupportChats, subscribeToSupportChatsByStatus } from '../firebase/supportChats'
import { useAuth } from '../context/authContext'
import Pagination from '../components/commons/Pagination'
import DialogSupportChat from '../components/dialogs/DialogSupportChat'

const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A'
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
}

const getStatusColor = (status) => {
    switch (status) {
        case 'active': return 'bg-green-500'
        case 'pending': return 'bg-yellow-500'
        case 'resolved': return 'bg-blue-500'
        case 'closed': return 'bg-gray-500'
        default: return 'bg-gray-500'
    }
}



const SupportChatItem = ({ chat, onUpdate, onViewChat, currentUser }) => {

    const unreadCount = chat.messages?.filter(msg => !msg.isRead && msg.senderType === 'user').length || 0
    const lastMessage = chat.messages?.[chat.messages.length - 1]

    return (
        <tr key={chat.id} className='h-[70px] hover:bg-[#2F2F2F] p-2'>
            <td className='pl-4'>
                <div className='flex items-center'>
                    <div className='w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold'>
                        {chat.userName?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className='ml-3'>
                        <div className='text-white font-medium'>{chat.userName}</div>
                        <div className='text-sm text-gray-400'>{chat.userEmail}</div>
                    </div>
                </div>
            </td>
            
            <td>
                <div className='flex flex-col'>
                    <span className='text-white font-medium'>
                        {lastMessage?.message?.substring(0, 50) || 'No messages'}...
                    </span>
                    <span className='text-sm text-gray-400'>
                        {lastMessage ? formatDate(lastMessage.timestamp) : 'N/A'}
                    </span>
                </div>
            </td>

            <td>
                <div className='flex items-center gap-2'>
                    <span className={`px-2 py-1 rounded-full text-xs text-white ${getStatusColor(chat.status)}`}>
                        {chat.status}
                    </span>
                    {unreadCount > 0 && (
                        <span className='bg-red-500 text-white text-xs rounded-full px-2 py-1'>
                            {unreadCount}
                        </span>
                    )}
                </div>
            </td>

            <td>
                <div className='text-sm text-gray-400'>
                    {formatDate(chat.createdAt)}
                </div>
            </td>

            <td>
                <div className='flex justify-end pr-4'>
                    <button
                        className="px-4 py-2 bg-blue-500 text-white rounded-[33px] text-sm"
                        onClick={() => onViewChat(chat)}
                    >
                        View Chat
                    </button>
                </div>
            </td>
        </tr>
    )
}

const SupportManagement = () => {
    const [chats, setChats] = useState([])
    const [loading, setLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const [selectedChat, setSelectedChat] = useState(null)
    const [showChatDialog, setShowChatDialog] = useState(false)
    const { user } = useAuth()

    const fetchChats = async () => {
        setLoading(true)
        try {
            const chatsData = await getAllSupportChats()
            setChats(chatsData)
        } catch (error) {
            console.error('Error fetching chats:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        let unsubscribe = null

        unsubscribe = subscribeToSupportChats((chatsData) => {
            setChats(chatsData)
            setLoading(false)
        })

        // Initial fetch
        fetchChats()

        // Cleanup subscription on unmount
        return () => {
            if (unsubscribe) {
                unsubscribe()
            }
        }
    }, [])

    const handleViewChat = (chat) => {
        setSelectedChat(chat)
        setShowChatDialog(true)
    }



    const itemsPerPage = 8
    const indexOfLastItem = currentPage * itemsPerPage
    const indexOfFirstItem = indexOfLastItem - itemsPerPage
    const currentChats = chats.slice(indexOfFirstItem, indexOfLastItem)

    const fields = ["User", "Last Message", "Status", "Created", "Actions"]

    return (
        <div className="min-h-full bg-[#0F0F0F] text-white">
            <div className="px-10 py-8">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <h1 className="text-primary text-[36px]">Chat Support Manager</h1>
                        <div className="flex items-center gap-2 text-green-400 text-sm">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            <span>Live</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="px-6 py-2 bg-[#1F1F1F] rounded-[33px] text-[#666] text-center">
                            {chats.length} Support Chats
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="flex flex-col items-center">
                            <img src="/assets/icons/icon-loading.svg" alt="loading" className="w-8 h-8 animate-spin mb-4" />
                            <p className="text-white text-lg">Loading support chats...</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="my-10">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-[#2F2F2F]">
                                        {fields.map((field, index) => (
                                            <th key={index} className="text-left py-4 text-[#666] font-medium">
                                                {field}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentChats.map((chat) => (
                                        <SupportChatItem
                                            key={chat.id}
                                            chat={chat}
                                            onUpdate={fetchChats}
                                            onViewChat={handleViewChat}
                                            currentUser={user}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        
                        <div className="mt-auto">
                            <Pagination 
                                itemsPerPage={itemsPerPage} 
                                totalItems={chats.length} 
                                onPageChange={(v) => setCurrentPage(v)} 
                            />
                        </div>
                    </>
                )}
            </div>

            {showChatDialog && selectedChat && (
                <DialogSupportChat
                    open={showChatDialog}
                    onClose={() => setShowChatDialog(false)}
                    chat={selectedChat}
                    currentUser={user}
                    onUpdate={fetchChats}
                />
            )}


        </div>
    )
}

export default SupportManagement 